import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { submitResult } from "@/lib/resultOutbox";
import { toast } from "sonner";
import { playSound } from "@/lib/sounds";
import { fireVictoryConfetti } from "@/components/gameplay/celebrations";
import { isWinningProgress, localSupertAward } from "@/lib/gamePlayMath";
import { computeVsOutcome } from "@/lib/duelOutcome";

/**
 * useVsHumanDuel — SYMMETRIC VS-HUMAN duel state machine.
 *
 * REDESIGNED (Feb 2026) to mirror VS ROBOT: both peers share the SAME
 * question deck + deterministic sequence (sent by the backend once at
 * match_start) and RACE against each other on each round. The round
 * resolves as soon as BOTH clicks are received (or forced by an internal
 * safety deadline). Uses `computeVsOutcome()` — the exact same pure
 * function VS ROBOT uses — so the outcome banners, +1 / +2 asymmetric
 * gains and simultaneous window all behave identically.
 *
 * The hook is a no-op when `enabled` is false.
 *
 * Shared refs (passed in via `sharedRefs`) live in GamePlay because they
 * are also used by the answer() handler: userClickRef, roundResolvedRef,
 * roundStartRef, progressRef.
 *
 * Returned API:
 *  - Rendered state: vshOpponent, oppProgress, vshOutcome, vshFloating,
 *    vshFx, vshAbandoned
 *  - Setup: setVshOpponent (initial load), sequenceRef, reset() (beginGame).
 *  - Refs consumed by answer() and spin: oppProgressRef, sequenceRef,
 *    vshFinishedRef, roundDeadlineRef
 *  - Resolvers: resolveRound (invoked by answer() when opponent has already
 *    responded, and internally when opponent_answer arrives).
 */
export function useVsHumanDuel({
  enabled,
  vshCtx,
  session,
  phase,
  spinning,
  round,
  playerLevel,
  gameId,
  profile,
  repeatsSoFar,
  sharedRefs,        // { userClickRef, roundResolvedRef, roundStartRef, progressRef }
  setProgress,
  setStreak,
  setPhase,
  setBusy,
  setFeedback,
  setReveal,
  setRound,
  animateSpin,
  paintVshRoundRef,
}) {
  const [vshOpponent, setVshOpponent] = useState(null);
  const [oppProgress, setOppProgress] = useState([]);
  const [vshOutcome, setVshOutcome] = useState(null);
  const [vshFloating, setVshFloating] = useState({ user: null, opponent: null });
  const [vshFx, setVshFx] = useState(null);
  const [vshAbandoned, setVshAbandoned] = useState(false);

  const oppClickRef = useRef(null);
  const oppProgressRef = useRef([]);
  const sequenceRef = useRef([]);
  const roundDeadlineRef = useRef(null);
  const vshFinishedRef = useRef(false);
  const vshDisconnectTimerRef = useRef(null);
  // Synchronized advance state (Mar 2026): the server's `round_result`
  // drives the round transition on BOTH clients at the same instant.
  const advancedRoundRef = useRef(0);        // highest round already advanced TO
  const lastOutcomeFinishedRef = useRef(false);

  // Keep refs in sync so async timer-fired callbacks read fresh values.
  useEffect(() => { oppProgressRef.current = oppProgress; }, [oppProgress]);

  // ---- Session / round / setter refs -----------------------------------
  // The WS subscriber is bound ONCE at mount (see effect below). Every
  // callback it needs to invoke MUST be read through a ref so the handler
  // always sees the latest closure — not the one from the first render.
  // Without this indirection, `phase`, `session`, `round`, etc. all get
  // frozen and the "opponent answered" branch will wrongly return early.
  const sessionRef = useRef(session);
  useEffect(() => { sessionRef.current = session; }, [session]);
  const phaseRef = useRef(phase);
  useEffect(() => { phaseRef.current = phase; }, [phase]);
  const roundRef = useRef(round);
  useEffect(() => { roundRef.current = round; }, [round]);
  const playerLevelRef = useRef(playerLevel);
  useEffect(() => { playerLevelRef.current = playerLevel; }, [playerLevel]);
  const repeatsSoFarRef = useRef(repeatsSoFar);
  useEffect(() => { repeatsSoFarRef.current = repeatsSoFar; }, [repeatsSoFar]);

  // Resets ALL VS-HUMAN state — called from beginGame and playAgain.
  const reset = () => {
    setOppProgress([]);
    oppProgressRef.current = [];
    oppClickRef.current = null;
    setVshOutcome(null);
    setVshFloating({ user: null, opponent: null });
    vshFinishedRef.current = false;
    advancedRoundRef.current = 0;
    lastOutcomeFinishedRef.current = false;
  };

  // ---- Synchronized round advance (Mar 2026) ----------------------------
  // advanceRound: transition to round `targetRound` painting the
  // deterministic (card, position) from the server's pre-baked rounds list.
  // The TARGET is computed at SCHEDULE time (not at fire time) so a late
  // fallback timer firing after the primary advance is a guaranteed no-op —
  // comparing against roundRef at fire time allowed a stale fallback to
  // skip an extra round and desync the duel (user report 2026-06).
  const advanceRound = (targetRound, nextIdxOpt) => {
    if (phaseRef.current !== "playing" || vshFinishedRef.current) return;
    if (advancedRoundRef.current >= targetRound) return;
    advancedRoundRef.current = targetRound;
    setFeedback(null);
    setReveal(null);
    setRound(targetRound);
    // Round N consumes rounds[N-1] (round numbers are 1-based).
    const nextIdx = nextIdxOpt != null ? nextIdxOpt : targetRound - 1;
    const paint = paintVshRoundRef?.current;
    const landing = typeof paint === "function" ? paint(nextIdx) : null;
    let force;
    if (landing && landing.position) {
      force = { level: playerLevelRef.current, position: landing.position };
    } else {
      const seqNext = sequenceRef.current[nextIdx];
      force = seqNext ? { level: playerLevelRef.current, position: seqNext.position } : undefined;
    }
    animateSpin(force);
    setBusy(false);
  };

  // scheduleAdvance: rAF timestamp-based scheduling — precise and identical
  // on both devices regardless of main-thread load (setTimeout drifts).
  // A setTimeout remains ONLY as a background-tab fallback (rAF pauses when
  // the tab is hidden). Double-fire is impossible (target-based guard).
  const scheduleAdvance = (delayMs, targetRound, nextIdx) => {
    const target = performance.now() + Math.max(0, delayMs);
    let rafId = null;
    let done = false;
    const fire = () => {
      if (done) return;
      done = true;
      if (rafId != null) cancelAnimationFrame(rafId);
      clearTimeout(toId);
      advanceRound(targetRound, nextIdx);
    };
    const tick = (now) => {
      if (done) return;
      if (now >= target - 1) fire();
      else rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    const toId = setTimeout(fire, Math.max(0, delayMs) + 150);
  };

  // ---- Per-round resolver (mirrors VS ROBOT.resolveRound). --------------
  // Normally called with the server's `round_result` (authoritative gains,
  // awards and progress for BOTH players — identical on both devices).
  // Without `server` (10s backstop only) it falls back to local rolls.
  const resolveRound = (server = null) => {
    const s = sessionRef.current;
    if (!s) return;
    if (sharedRefs.roundResolvedRef.current) return;
    sharedRefs.roundResolvedRef.current = true;
    if (roundDeadlineRef.current) { clearTimeout(roundDeadlineRef.current); roundDeadlineRef.current = null; }

    const u = sharedRefs.userClickRef.current;   // { idx, time, correct }
    const opp = oppClickRef.current;              // { idx, time, correct }
    const oppName = vshOpponent?.name || "Adversário";
    const SIMUL_MS = Math.max(0, Math.min(2000, Number(s?.game?.simultaneous_ms ?? 500)));

    const local = computeVsOutcome(
      u,
      opp,
      { simulMs: SIMUL_MS, oppName, oppPossessive: "ele", allowOppNull: true }
    );
    const userGain = server ? Number(server.you_gain || 0) : local.userGain;
    const oppGain = server ? Number(server.opp_gain || 0) : local.oppGain;
    const { outcomeText, outcomeSound } = local;

    // Enrich the feedback with simultaneous/oppFirst flags so <Options />
    // renders lighter/darker shades AND locks itself when opponent answered
    // first — mirroring VS ROBOT visuals.
    const oppFirst = !u && !!opp;
    const simultaneous = !!u && !!opp && Math.abs((u.time || 0) - (opp.time || 0)) < SIMUL_MS;
    if (oppFirst) {
      setFeedback(() => ({
        correctIndex: opp?.correctIndex ?? null,
        chosen: null,
        correct: false,
        robotFirst: true,
        simultaneous: false,
      }));
    } else {
      setFeedback((prev) => (prev ? { ...prev, simultaneous, robotFirst: false } : prev));
    }

    setStreak((st) => (userGain > oppGain ? st + 1 : 0));
    setVshOutcome({ text: outcomeText });
    if (outcomeSound === "correct") playSound(s?.game?.correct_sound || "correct", s?.game?.correct_sound_volume);
    else if (outcomeSound === "wrong") playSound(s?.game?.wrong_sound || "wrong", s?.game?.wrong_sound_volume);

    // SUPERT tokens: authoritative from the server (`round_result` carries the
    // awards + full progress of BOTH players, rolled once server-side), so
    // the two devices always show identical collections. Local roll only on
    // the backstop path (server message lost).
    let userProg = sharedRefs.progressRef.current.slice();
    let oppProg = oppProgressRef.current.slice();
    let userAwards = [];
    let oppAwards = [];
    if (server) {
      userProg = Array.isArray(server.you_progress) ? server.you_progress.slice() : userProg;
      oppProg = Array.isArray(server.opp_progress) ? server.opp_progress.slice() : oppProg;
      userAwards = Array.isArray(server.you_awards) ? server.you_awards : [];
      oppAwards = Array.isArray(server.opp_awards) ? server.opp_awards : [];
      repeatsSoFarRef.current += userAwards.filter((a) => a.was_repeat).length;
    } else {
      for (let i = 0; i < userGain; i++) {
        const a = localSupertAward({
          progress: userProg,
          mode: s.game.mode || "normal",
          repeats_so_far: repeatsSoFarRef.current,
          max_repeated_letters: s.game.max_repeated_letters || 0,
        });
        userProg = a.progress;
        userAwards.push({ item: a.item, was_repeat: a.was_repeat });
      }
      for (let i = 0; i < oppGain; i++) {
        const a = localSupertAward({
          progress: oppProg,
          mode: s.game.mode || "normal",
          repeats_so_far: 0,
          max_repeated_letters: s.game.max_repeated_letters || 0,
        });
        oppProg = a.progress;
        oppAwards.push({ item: a.item, was_repeat: a.was_repeat });
      }
    }
    for (let i = 0; i < userGain; i++) {
      // Fire-and-forget stats
      api.post("/play/award", {
        progress: sharedRefs.progressRef.current,
        mode: s.game.mode || "normal",
        repeats_so_far: repeatsSoFarRef.current,
        max_repeated_letters: s.game.max_repeated_letters || 0,
      }).catch(() => { /* stats only */ });
    }
    setProgress(userProg);
    sharedRefs.progressRef.current = userProg;
    setOppProgress(oppProg);
    oppProgressRef.current = oppProg;

    // Redundancy net for the backstop path only (server already synced both).
    if (!server) {
      try { vshCtx.send({ type: "progress", progress: userProg, awards: userAwards }); } catch { /* */ }
    }

    // Floating letter feedback (both sides).
    if (oppGain > 0) setVshFx("bounce");
    else if (userGain > 0 && opp && !opp.correct) setVshFx("shake");
    setTimeout(() => setVshFx(null), 700);

    setVshFloating({ user: userAwards, opponent: oppAwards });
    const filterKeepers = (arr) =>
      arr && arr.length ? arr.filter((a) => a.item === "*" || !a.was_repeat) : arr;
    setTimeout(() => {
      setVshFloating((cur) => ({
        user: filterKeepers(cur.user),
        opponent: filterKeepers(cur.opponent),
      }));
    }, 1000);
    setTimeout(() => setVshFloating({ user: null, opponent: null }), 2200);

    const sup = s.game.supert_count || 1;
    const userWon = server ? !!server.you_won : isWinningProgress(userProg, s.game.mode, sup);
    const oppWon = server ? !!server.opp_won : isWinningProgress(oppProg, s.game.mode, sup);
    lastOutcomeFinishedRef.current = userWon || oppWon;

    // Helper: politely inform the server we're leaving so it doesn't keep
    // us "in match". Delayed so BOTH clients transition first.
    const sendLeaveAfterFinish = () => {
      setTimeout(() => {
        try { vshCtx.send({ type: "leave" }); } catch { /* */ }
      }, 1500);
    };

    if (userWon || oppWon) {
      // End-of-game path — brief pause so the outcome banner is readable.
      setTimeout(() => {
        if (userWon && oppWon) {
          fireVictoryConfetti();
          playSound(s?.game?.victory_sound || "victory");
          setPhase("won"); setBusy(false);
        } else if (userWon) {
          fireVictoryConfetti();
          playSound(s?.game?.victory_sound || "victory");
          setPhase("won"); setBusy(false);
        } else {
          playSound(s?.game?.defeat_sound || "defeat");
          setPhase("lost"); setBusy(false);
        }
        sendLeaveAfterFinish();
      }, 900);
    } else if (!server) {
      // Backstop path only (server `round_result` lost): self-schedule the
      // advance. Target computed NOW so a late fire is a guaranteed no-op.
      const roundDelayMs = Math.max(0, Math.min(5000, Number(s?.game?.round_delay_ms ?? 100)));
      scheduleAdvance(roundDelayMs + 1500, roundRef.current + 1, null);
    }
  };

  // Safety deadline (armed once our answer is confirmed by the server): if
  // the server's round_result doesn't arrive in 10s, resolve locally with
  // whatever we know so the duel NEVER stalls forever.
  const armRoundDeadline = () => {
    if (roundDeadlineRef.current || sharedRefs.roundResolvedRef.current) return;
    roundDeadlineRef.current = setTimeout(() => {
      roundDeadlineRef.current = null;
      if (!sharedRefs.roundResolvedRef.current) resolveRound(null);
    }, 10000);
  };

  // Server-authoritative round result: the round is decided by the FIRST
  // answer (VS ROBOT rule). Carries both answers (server-ordered `t_ms`),
  // gains, awards, full progress, win flags, the next round index and the
  // shared advance delay — the single source of truth on both clients.
  const handleRoundResult = (msg) => {
    if (msg.round && msg.round !== roundRef.current) return;
    const mapAns = (a) => (a ? {
      idx: Number(a.answer_index || 0),
      time: typeof a.t_ms === "number" ? a.t_ms : (Number(a.elapsed_seconds) || 0) * 1000,
      correct: !!a.correct,
      correctIndex: typeof a.correct_index === "number" ? a.correct_index : null,
    } : null);
    sharedRefs.userClickRef.current = msg.you ? mapAns(msg.you) : null;
    oppClickRef.current = msg.opponent ? mapAns(msg.opponent) : null;
    if (!sharedRefs.roundResolvedRef.current) resolveRound(msg);
    if (!lastOutcomeFinishedRef.current) {
      scheduleAdvance(Number(msg.advance_in_ms ?? 100), msg.round + 1, msg.next_round_index ?? null);
    }
  };

  // ---- Effect: subscribe to WS messages (uses refs to avoid stale state)
  useEffect(() => {
    if (!enabled) return undefined;
    const off = vshCtx.subscribe((msg) => {
      if (!msg || !msg.type) return;
      if (msg.type === "answer_result") {
        // Round scoping: a late echo from a previous round must not leak in,
        // nor override the lock feedback of an already-resolved round.
        if (msg.round && msg.round !== roundRef.current) return;
        if (sharedRefs.roundResolvedRef.current) return;
        // Server echoed our answer's correctness. Enrich userClickRef and
        // update visual feedback for the answered button.
        if (sharedRefs.userClickRef.current) {
          sharedRefs.userClickRef.current = {
            ...sharedRefs.userClickRef.current,
            correct: !!msg.correct,
          };
        }
        setFeedback({
          correct: !!msg.correct,
          correctIndex: typeof msg.correct_index === "number" ? msg.correct_index : null,
          chosen: typeof msg.answer_index === "number" ? msg.answer_index : (sharedRefs.userClickRef.current?.idx ?? null),
        });
        armRoundDeadline();
      } else if (msg.type === "opponent_answer") {
        if (msg.round && msg.round !== roundRef.current) return;
        if (sharedRefs.roundResolvedRef.current) return;
        oppClickRef.current = {
          time: (Number(msg.elapsed_seconds) || 0) * 1000,
          correct: !!msg.correct,
          idx: Number(msg.answer_index || 0),
          correctIndex: typeof msg.correct_index === "number" ? msg.correct_index : null,
        };
        // Opponent clicked first → lock our options at once (round_result
        // follows a few ms later with the authoritative outcome).
        if (!sharedRefs.userClickRef.current) {
          setFeedback({
            correctIndex: typeof msg.correct_index === "number" ? msg.correct_index : null,
            chosen: null,
            correct: false,
            robotFirst: true,
            simultaneous: false,
          });
        }
      } else if (msg.type === "round_result") {
        handleRoundResult(msg);
      } else if (msg.type === "opponent_progress") {
        // Reconciliation fallback: adopt whichever progress is FURTHER along.
        // In normal play both peers compute identical outcomes so this is
        // a no-op; only fires when a WS drop caused divergence.
        const newProg = Array.isArray(msg.progress) ? msg.progress : [];
        if (newProg.length > oppProgressRef.current.length) {
          oppProgressRef.current = newProg;
          setOppProgress(newProg);
        }
      } else if (msg.type === "opponent_left") {
        const ph = phaseRef.current;
        // Guard: if the round result already finished the game (server sent
        // `finished:true` before this `opponent_left`), the duel is already
        // decided — do NOT overwrite the pending phase transition with the
        // abandonment banner. This happens in the rematch flow where the
        // winner's `type:"leave"` triggers `opponent_left` on the peer whose
        // `phase` is still `"playing"` inside the 900ms result-animation
        // window.
        if (lastOutcomeFinishedRef.current) return;
        if (ph === "playing" || ph === "ready" || ph === "countdown") {
          toast.success("O adversário abandonou — vitória registada.");
          setVshAbandoned(true);
          setPhase("won");
        }
      }
    });
    return off;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, vshCtx]);

  // ---- Effect: 15-second grace on disconnect during a duel --------------
  useEffect(() => {
    if (!enabled) return undefined;
    if (vshCtx.connected) {
      if (vshDisconnectTimerRef.current) {
        clearTimeout(vshDisconnectTimerRef.current);
        vshDisconnectTimerRef.current = null;
      }
      return undefined;
    }
    if (phase !== "playing") return undefined;
    vshDisconnectTimerRef.current = setTimeout(() => {
      if (!vshCtx.connected && phaseRef.current === "playing") {
        toast.info("Ligação perdida por mais de 15s — duelo descartado.");
        setVshAbandoned(true);
        setPhase("lost");
      }
    }, 15000);
    return () => {
      if (vshDisconnectTimerRef.current) {
        clearTimeout(vshDisconnectTimerRef.current);
        vshDisconnectTimerRef.current = null;
      }
    };
  }, [enabled, vshCtx.connected, phase]);

  // ---- Effect: reset per-round VS HUMANO signals at the start of each round
  useEffect(() => {
    if (!enabled || phase !== "playing" || spinning) return;
    sharedRefs.userClickRef.current = null;
    oppClickRef.current = null;
    sharedRefs.roundResolvedRef.current = false;
    setVshOutcome(null);
    sharedRefs.roundStartRef.current = performance.now();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, phase, spinning, round]);

  // ---- Effect: persist the duel result once when the duel ends ----------
  useEffect(() => {
    if (!enabled || vshFinishedRef.current || !session) return;
    if (phase !== "won" && phase !== "lost") return;
    if (vshAbandoned) {
      vshFinishedRef.current = true;
      return;
    }
    vshFinishedRef.current = true;
    const sup = session.game.supert_count || 1;
    const userWon = isWinningProgress(sharedRefs.progressRef.current, session.game.mode, sup);
    const oppWon = isWinningProgress(oppProgressRef.current, session.game.mode, sup);
    const winner = (userWon && oppWon) ? "tie" : (phase === "won" ? "user" : "opponent");
    submitResult("/vs-human/finish", {
      session_id: session.session_id,
      game_id: gameId,
      user_id: vshCtx.userId || "",
      name: profile.name,
      opponent_user_id: vshOpponent?.user_id || "",
      opponent_name: vshOpponent?.name || "",
      winner,
      user_letters: sharedRefs.progressRef.current.filter((p) => p !== "*").length,
      user_stars: sharedRefs.progressRef.current.filter((p) => p === "*").length,
      opponent_letters: oppProgressRef.current.filter((p) => p !== "*").length,
      opponent_stars: oppProgressRef.current.filter((p) => p === "*").length,
      rounds: round,
    }, { description: `VS Humano — ${profile.name} vs ${vshOpponent?.name || "?"}` });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, phase, session, vshOpponent, profile.name, gameId, round, vshCtx.userId, vshAbandoned]);

  return {
    // Rendered state
    vshOpponent, oppProgress, vshOutcome, vshFloating, vshFx, vshAbandoned,
    // Setup hooks (initial load + reset)
    setVshOpponent,
    setVshFloating,
    reset,
    // Refs consumed by answer() / spin handler
    sequenceRef,
    oppProgressRef,
    oppClickRef,
    vshFinishedRef,
    roundDeadlineRef,
    // Resolvers (called from answer())
    resolveRound,
  };
}
